import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { Trash2, ShoppingBag, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";

export default function Cart() {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart`);
      setCartItems(response.data);
    } catch (e) {
      toast.error("Failed to load cart");
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (itemId) => {
    try {
      await axios.delete(`${API}/cart/${itemId}`);
      setCartItems((items) => items.filter((item) => item.id !== itemId));
      toast.success("Removed from cart");
    } catch (e) {
      toast.error("Failed to remove item");
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart`);
      setCartItems([]);
      toast.success("Cart cleared");
    } catch (e) {
      toast.error("Failed to clear cart");
    }
  };

  const handleCheckout = async () => {
    setCheckingOut(true);
    try {
      const response = await axios.post(`${API}/checkout/session`, {
        origin_url: window.location.origin,
      });

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setCheckingOut(false);
    }
  };

  const total = cartItems.reduce((sum, item) => {
    return sum + (item.design?.price || 0);
  }, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-32 container-padding">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white p-6 rounded-lg border border-zinc-200">
                  <div className="flex gap-4">
                    <div className="w-24 h-24 bg-zinc-200 rounded"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-zinc-200 rounded w-1/2"></div>
                      <div className="h-3 bg-zinc-200 rounded w-1/4"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background noise-bg" data-testid="cart-page">
      <Navbar />

      <main className="pt-32 pb-16 container-padding">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Continue Shopping
            </button>
            <h1 
              className="text-3xl sm:text-4xl font-semibold tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Shopping Cart
            </h1>
            <p className="mt-2 text-zinc-600">
              {cartItems.length} {cartItems.length === 1 ? "item" : "items"} in your cart
            </p>
          </div>

          {cartItems.length === 0 ? (
            <div className="empty-state bg-white rounded-2xl border border-zinc-200 py-16">
              <ShoppingBag className="w-16 h-16 text-zinc-300 mb-4" />
              <h3 className="text-xl font-medium mb-2">Your cart is empty</h3>
              <p className="text-zinc-500 mb-6">
                Looks like you haven't added any designs yet.
              </p>
              <Link to="/marketplace" className="btn-primary inline-flex items-center gap-2">
                Browse Designs
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {cartItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white p-6 rounded-xl border border-zinc-200 flex gap-6"
                    data-testid={`cart-item-${item.id}`}
                  >
                    {/* Image */}
                    <Link to={`/design/${item.design?.id}`} className="shrink-0">
                      <div className="w-24 h-24 rounded-lg overflow-hidden bg-zinc-100">
                        <img
                          src={item.design?.image_url}
                          alt={item.design?.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </Link>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <Link 
                        to={`/design/${item.design?.id}`}
                        className="font-medium hover:text-indigo-600 transition-colors line-clamp-1"
                      >
                        {item.design?.title}
                      </Link>
                      <p className="text-sm text-zinc-500 mt-1">
                        {item.design?.category}
                      </p>
                      <p className="price-tag mt-2 text-lg">
                        ${item.design?.price?.toFixed(2)}
                      </p>
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={() => removeItem(item.id)}
                      className="shrink-0 p-2 text-zinc-400 hover:text-red-500 transition-colors"
                      data-testid={`remove-item-${item.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}

                {/* Clear Cart */}
                <button
                  onClick={clearCart}
                  className="text-sm text-zinc-500 hover:text-red-500 transition-colors"
                  data-testid="clear-cart-btn"
                >
                  Clear Cart
                </button>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white p-6 rounded-xl border border-zinc-200 sticky top-28">
                  <h3 className="font-medium mb-6">Order Summary</h3>

                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-600">Subtotal</span>
                      <span className="price-tag">${total.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-600">Tax</span>
                      <span className="text-zinc-600">Calculated at checkout</span>
                    </div>
                  </div>

                  <div className="border-t border-zinc-200 pt-4 mb-6">
                    <div className="flex justify-between">
                      <span className="font-medium">Total</span>
                      <span className="font-semibold price-tag text-lg">${total.toFixed(2)}</span>
                    </div>
                  </div>

                  <button
                    onClick={handleCheckout}
                    disabled={checkingOut}
                    className="w-full btn-primary flex items-center justify-center gap-2"
                    data-testid="checkout-btn"
                  >
                    {checkingOut ? (
                      <>
                        <Loader2 className="w-5 h-5 spinner" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Checkout
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-zinc-500 text-center mt-4">
                    Secure payment powered by Stripe
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
